// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from "react";
import { connect } from "react-redux";
import {
    FontIcon,
    CommandBarButton,
    IButtonStyles,
    IIconProps,
    IOverflowSetItemProps,
    OverflowSet,
    Customizer,
    ICustomizations,
    Separator,
    ContextualMenuItemType,
    Link,
    ActionButton,
    IconButton,
    Label,
    CommandButton,
    PrimaryButton,
    Checkbox,
    Dialog,
    DialogContent,
    DialogFooter,
    DefaultButton,
    DialogType,
    Spinner,
    SpinnerSize,
} from "@fluentui/react";
import { IAdHocGroupProject, IAdHocProject, IApplicationState, IProject, IProjecToPost } from "../../../models/applicationState";
import { PlatformType, isElectron } from "../../../common/hostProcess";
import { getLightGreyTheme, getSubMenuTheme } from "../../../common/themes";
import "./titleBar.scss";
import FileUploaderDialog from "../common/fileUploaderDialog/fileUploaderDialog";
import { getCookieName } from "../common/jsonSchemaFormHelper";
import { History } from "history";
import { NavLink } from "react-router-dom";
import Tooltip from 'react-tooltip'
import ReactTooltip from "react-tooltip";

const isElectronCheck = isElectron();
const platform =
    global && global.process && global.process.platform
        ? global.process.platform
        : PlatformType.Web;
const isMac = platform === "darwin";
const isLinux = platform === "linux";
const isElectronAndNotMacOrLinux = isElectronCheck && !(isMac || isLinux);
const isNotElectronAndMacOrLinux = !(isElectronCheck && (isMac || isLinux));

export interface ITitleBarProps extends React.Props<TitleBarV2> {
    icon?: string | JSX.Element;
    title?: string;
    project?: IProject;
    history?: History;
    onUploadComplete?: () => void;
    widgetMode?: boolean;
    adHocOcr?: IAdHocProject;
    postAdHocToSource?: () => void;
    canGenerateModel: boolean;
    onGenerateModelChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    runGenerateModel: boolean;
    hasExistingModel: boolean;
    adHocOcrV2: IAdHocProject;
    adHocOcrGroup: IAdHocGroupProject;
    adHocOcrV3: IAdHocProject;
    projectToPosts?: IProjecToPost[];
    toggleHidePostDialog?:() => void;
    hidePostDialog: boolean;
    postToSourceV3?: () => void;
    groupId?: string;
    isPosting?: boolean;
}

export interface ITitleBarState {
    maximized: boolean;
    fullscreen: boolean;
    menu: Electron.Menu;
    showFileUpload: boolean;        
}

function mapStateToProps(state: IApplicationState) {
    return {
        title: state.appTitle,
    };
}

@connect(mapStateToProps, null)
export class TitleBarV2 extends React.Component<ITitleBarProps, ITitleBarState> {
    public state: ITitleBarState = {
        maximized: false,
        fullscreen: false,
        menu: null,
        showFileUpload: false        
    };

    private remote: Electron.Remote;
    private currentWindow: Electron.BrowserWindow;

    public componentDidMount() {
        if (isElectronCheck) {
            this.remote = window.require("electron").remote as Electron.Remote;
            this.currentWindow = this.remote.getCurrentWindow();
            this.currentWindow.on("maximize", () => this.onMaximize(true));
            this.currentWindow.on("unmaximize", () => this.onMaximize(false));
            this.currentWindow.on("enter-full-screen", () =>
                this.onFullScreen(true)
            );
            this.currentWindow.on("leave-full-screen", () =>
                this.onFullScreen(false)
            );

            this.setState({
                maximized: this.currentWindow.isMaximized(),
                fullscreen: this.currentWindow.isFullScreen(),
                menu: this.remote.Menu.getApplicationMenu(),
            });
        }
    }

    capitalizeFirstLetter(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    public render() {
        if (this.state.fullscreen) {
            return null;
        }

        const titleBarTheme: ICustomizations = {
            settings: {
                theme: getLightGreyTheme(),
            },
            scopedSettings: {},
        };

        const onRenderItem = (item: IOverflowSetItemProps): JSX.Element => {
            const buttonStyles: Partial<IButtonStyles> = {
                root: {
                    padding: "0 5px",
                    alignSelf: "stretch",
                },
            };
            const iconStyles: Partial<IIconProps> = {
                style: { display: "none" },
            };

            return (
                <CommandBarButton
                    menuProps={item.subMenuProps}
                    styles={buttonStyles}
                    text={item.name}
                    menuIconProps={iconStyles}
                />
            );
        };

        const onRenderOverflowButton = (
            overflowItems: any[] | undefined
        ): JSX.Element => {
            return (
                <CommandBarButton
                    ariaLabel="More items"
                    role="menuitem"
                    menuProps={{ items: overflowItems! }}
                />
            );
        };

        const onEditClick = () => {
            this.props.history.push(
                `/projects/${this.props.project.id}settings`
            );
        };

        const name = getCookieName();

        let showGenerateModel = !!this.props.adHocOcrV2 || this.props.canGenerateModel || !!this.props.adHocOcrV3;        
        let fontLabel = '';
        let disablePostButton = false;
        if (this.props.adHocOcr) {
            fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcr.source)}`;
        }
        if (this.props.adHocOcrV2) {
            fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcrV2.source)}`;
        }

        let subText = 'Are you sure you want to post the following documents?';
        if (this.props.adHocOcrV3) {
            if (this.props.projectToPosts && this.props.projectToPosts.length > 0) {
                fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcrV3.source)} (${this.props.projectToPosts.length})`;
                // subText += this.props.projectToPosts
                //     .map((p) => `\n${p.fileName}`) // Add each file name on a new line
                //     .join(""); // Combine into a single string
                // this.props.projectToPosts.forEach((p) => {
                //     subText += `\r\n${p.fileName}`;
                // })
            }
            else {
                fontLabel = `Post to ${this.capitalizeFirstLetter(this.props.adHocOcrV3.source)}`;         
                if (this.props.groupId) {                    
                    disablePostButton = true;   
                }
                else {
                    disablePostButton = false;   
                }
                //subText = 'Are you sure you want to post all processed documents?';
            }            
        }

        

        let modelDefaultChecked = false;
        if (this.props.adHocOcrV2 && !this.props.adHocOcrV2.hasModel && !this.props.adHocOcrV2.hasRunningRequest)
            modelDefaultChecked = true;
        if (this.props.adHocOcrV3 && !this.props.adHocOcrV3.hasModel && !this.props.adHocOcrV3.hasRunningRequest)
            modelDefaultChecked = true;

        const dialogContentProps = {
            type: DialogType.normal,
            title: this.props.adHocOcrV3 && this.props.adHocOcrV3.source ? `Post to ${this.capitalizeFirstLetter(this.props.adHocOcrV3.source)}` : '',
            subText: subText,
        };

        return (
            // hachet
            <div className="title-bar">
                <style>
                    {`
                        .checkbox-63 {
                            border: 1px solid #fff !important;
                        }              
                        .checkbox-63:hover {
                            border: 1px solid #ccc !important;
                        }              
                        .root-60:hover .ms-Checkbox-text {
                            color: #ccc !important;
                        }    
                        .run-generate-model-cb-parent {
                            padding-top: 7px;
                            padding-right: 15px;                            
                        }    
                        .run-generate-model-cb-parent:hover .ms-Checkbox-text {
                            color: #ccc !important;
                        }
                        .root-84:hover .ms-Checkbox-text {
                            color: rgb(50, 49, 48);
                        }
                        .is-checked i {
                            color: #fff;
                        }
                        .ms-Checkbox-checkbox {
                            border-color: #fff !important;
                        }
                        .run-generate-model-cb-parent span {
                            /* your styles here */
                            color: #fff !important;
                        }
                    `}
                </style>
                {/*<button onClick={() => this.props.onUploadComplete()}>*/}
                {/*    Upload Complete*/}
                {/*</button>*/}
                {isNotElectronAndMacOrLinux && (
                    <div className="title-bar-icon">
                        <FontIcon iconName="TagGroup" />
                    </div>
                )}
                {this.props.widgetMode && showGenerateModel &&
                    <React.Fragment>                        
                        <PrimaryButton
                            ariaDescription={fontLabel}
                            onClick={this.props.postAdHocToSource}
                            className="save-to-optimus"
                            disabled={disablePostButton || this.props.isPosting} // Disable the button while posting
                        >
                            {this.props.isPosting ? (
                                // Show a loading spinner when isPosting is true
                                <Spinner
                                    size={SpinnerSize.small}
                                    styles={{
                                        root: {
                                            marginRight: "8px", // Add spacing between the spinner and the text
                                        },
                                    }}
                                />
                            ) : (
                                // Show the save icon when not posting
                                <FontIcon
                                    iconName="Save"
                                    title={fontLabel}
                                    style={{
                                        marginRight: "8px", // Add spacing between the icon and the text
                                    }}
                                />
                            )}
                            &nbsp;
                            {fontLabel} {/* {this.props.isPosting ? "Posting..." : fontLabel}  */}
                        </PrimaryButton>
                        <div className="title-bar-icon"></div>
                        <div className="title-bar-main">
                            {this.props.adHocOcr && (
                                <Label className="adhoc-project-name">Document Service Id: {this.props.adHocOcr.project.name}</Label>                            
                            )}
                        </div>
                        <div className="title-bar-controls">
                            {/* <FontIcon
                                className="app-close-icon pull-right"
                                iconName="Cancel"
                                onClick={this.closeWindow}
                            /> */}
                            { showGenerateModel && !this.props.adHocOcrGroup && !this.props.adHocOcrV3?.hideGenerateModel &&
                                <div className="generate-model-checkbox" data-tip="React-tooltip">
                                    <Checkbox 
                                        label="Generate Model" 
                                        defaultChecked={modelDefaultChecked} 
                                        //checked={this.props.runGenerateModel || !this.props.adHocOcrV2?.hasModel} 
                                        className="pull-right run-generate-model-cb-parent" 
                                        onChange={this.props.onGenerateModelChange} 
                                        disabled={(this.props.adHocOcrV2 && this.props.adHocOcrV2.hasRunningRequest) || this.props.adHocOcrV3 && this.props.adHocOcrV3.hasRunningRequest}                                        
                                    />
                                    {this.props.adHocOcrV2 && this.props.adHocOcrV2.hasRunningRequest  && 
                                        <ReactTooltip place="left" type="light" effect="solid">
                                            <FontIcon iconName="Info12" /> &nbsp;This feature is temporary unavailable since there is a running request for new model
                                        </ReactTooltip>
                                    }
                                    {this.props.adHocOcrV3 && this.props.adHocOcrV3.hasRunningRequest  && 
                                        <ReactTooltip place="left" type="light" effect="solid">
                                            <FontIcon iconName="Info12" /> &nbsp;This feature is temporary unavailable since there is a running request for new model
                                        </ReactTooltip>
                                    }
                                </div>                                
                            }         
                            {/* <PrimaryButton ariaDescription="Generate Auto Label Model" onClick={this.props.postAdHocToSource} className="save-to-optimus" >
                                <FontIcon 
                                        iconName='Save' 
                                        
                                        title="Generate Model"                                
                                />                        
                                &nbsp;
                                Generate Model
                            </PrimaryButton> */}
                        </div>
                        <Dialog
                            hidden={this.props.hidePostDialog}
                            onDismiss={this.props.toggleHidePostDialog}
                            dialogContentProps={{
                                ...dialogContentProps,
                                styles: {
                                    title: {
                                        fontSize: "18px", // Larger font size for the title
                                        fontWeight: "bold", // Bold title
                                        color: "#333", // Darker title color
                                    },
                                    subText: {
                                        fontSize: "16px", // Slightly larger font for subtext
                                        color: "#666", // Subtle gray color for subtext
                                        marginBottom: "20px", // Add spacing below subtext
                                        marginTop: "10px", // Add spacing above subtext
                                    },
                                },
                            }}
                            modalProps={{
                                isBlocking: true,
                                styles: {
                                    main: {
                                        maxWidth: "500px", // Limit the dialog width
                                        borderRadius: "8px", // Rounded corners for the dialog
                                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)", // Add a subtle shadow
                                    },
                                },
                            }}
                        >
                            <div style={{ marginBottom: "20px", marginTop: "20px" }}>
                                <ul style={{ paddingLeft: "20px", margin: "0", listStyleType: "disc" }}>
                                    {this.props.projectToPosts &&
                                        this.props.projectToPosts.length > 0 &&
                                        this.props.projectToPosts.map((p) => (
                                            <li
                                                key={p.projectId}
                                                style={{
                                                    fontSize: "14px", // Smaller font size for list items
                                                    color: "#333", // Darker text color
                                                    marginBottom: "8px", // Add spacing between list items
                                                }}
                                            >
                                                {p.fileName}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                            <DialogFooter>
                                <PrimaryButton
                                    text="Yes"
                                    onClick={() => { this.props.postToSourceV3()}}
                                    styles={{
                                        root: {
                                            backgroundColor: "#0078d4", // Blue background
                                            color: "#fff", // White text
                                            borderRadius: "4px", // Rounded corners
                                            padding: "10px 20px", // Add padding for better spacing
                                            fontSize: "14px", // Slightly larger font size
                                        },
                                        rootHovered: {
                                            backgroundColor: "#005a9e", // Darker blue on hover
                                        },
                                    }}
                                />
                                <DefaultButton
                                    text="No"
                                    onClick={this.props.toggleHidePostDialog}
                                    styles={{
                                        root: {
                                            borderRadius: "4px", // Rounded corners
                                            padding: "10px 20px", // Add padding for better spacing
                                            fontSize: "14px", // Slightly larger font size
                                        },
                                    }}
                                />
                            </DialogFooter>
                        </Dialog>
                        {/* <Dialog                            
                            hidden={this.props.hidePostDialog}
                            onDismiss={this.props.toggleHidePostDialog}
                            dialogContentProps={dialogContentProps}
                            modalProps={{
                                isBlocking: true,
                            }}
                        >
                            <ul>
                                {this.props.projectToPosts && this.props.projectToPosts.length > 0 && this.props.projectToPosts.map((p) => (
                                    <li key={p.projectId}>
                                        {p.fileName}
                                    </li>
                                ))}
                            </ul>
                            <DialogFooter>
                                <PrimaryButton
                                    text="Yes" />
                                <DefaultButton
                                    text="No" onClick={() => this.props.toggleHidePostDialog()}/>
                            </DialogFooter>
                        </Dialog> */}
                    </React.Fragment>
                }                
                
            </div>
        );
    }

    private addDefaultMenuItems = (menu: Electron.Menu) => {
        if (!menu) {
            return null;
        }

        return menu.items.reduce(this.renderMenuItem, []);
    };

    private showUpload = () => {
        this.setState({
            showFileUpload: true,
        });
    };

    private renderMenuItem = (results, menuItem: Electron.MenuItem) => {
        if (!menuItem.visible) {
            return null;
        }

        const itemType: string = menuItem["type"];

        switch (itemType) {
            case "separator":
                results.push({
                    key: menuItem.label,
                    itemType: ContextualMenuItemType.Divider,
                });
                break;
            case "submenu":
                results.push({
                    key: menuItem.label,
                    name: menuItem.label,
                    subMenuProps: {
                        theme: getSubMenuTheme(),
                        items: this.addDefaultMenuItems(menuItem["submenu"]),
                    },
                });
                break;
            case "normal":
                results.push({
                    key: menuItem.label,
                    name: menuItem.label,
                    onClick: (e) => this.onMenuItemClick(e, menuItem),
                });
        }
        return results;
    };

    private onMenuItemClick(e: any, menuItem: Electron.MenuItem) {
        if (menuItem.label === "Zoom In") {
            this.currentWindow.webContents.setZoomLevel(
                this.currentWindow.webContents.getZoomLevel() + 0.3
            );
        } else if (menuItem.label === "Zoom Out") {
            this.currentWindow.webContents.setZoomLevel(
                this.currentWindow.webContents.getZoomLevel() - 0.3
            );
        } else if (menuItem.label === "Reset Zoom") {
            this.currentWindow.webContents.setZoomLevel(0);
        } else if (menuItem.click) {
            menuItem.click.call(menuItem, menuItem, this.currentWindow);
        }
    }

    private onMaximize = (isMaximized: boolean) => {
        this.setState({
            maximized: isMaximized,
        });
    };

    private onFullScreen = (isFullScreen: boolean) => {
        this.setState({
            fullscreen: isFullScreen,
        });
    };

    private minimizeWindow = () => {
        this.currentWindow.minimize();
    };

    private maximizeWindow = () => {
        this.currentWindow.maximize();
    };

    private unmaximizeWindow = () => {
        this.currentWindow.unmaximize();
    };

    private closeWindow = () => {
        this.currentWindow.close();
    };
}
